/**
 * Native bridge — every Capacitor feature behind one seam, with graceful
 * no-op fallbacks on the web. Components call these helpers freely; on a
 * browser they simply do nothing (or use the closest web API).
 *
 * Plugins used (install before `npx cap sync`):
 *   npm i @capacitor/haptics @capacitor/local-notifications @capacitor/share
 *   npm i @capacitor/status-bar @capacitor/splash-screen
 */

import { Capacitor } from '@capacitor/core';

export const isNative = (): boolean => Capacitor.isNativePlatform();
/** iOS shell only — Sign in with Apple is offered, the invite-code field is hidden (App Review 3.1.1 / 4.8). */
export const isIOS = (): boolean => Capacitor.getPlatform() === 'ios';
/** 'ios' | 'android' | 'web' — for support reports (engine/report.ts). */
export const platformName = (): string => Capacitor.getPlatform();

/** Where the standalone pages (guide, privacy, terms, home) are served. */
export const PUBLIC_ORIGIN = 'https://zonein-82a22.web.app';

/**
 * A link to one of our standalone pages that works in every shell.
 *
 * 12 Oct 2026 (Chad, iOS build 2.2.0 (7)): "Open the User Guide" did nothing.
 * MEASURED in @capacitor/ios 7.6.9 WebViewDelegationHandler.swift: a
 * target="_blank" link asks for a new window (createWebViewWith), which
 * Capacitor hands to UIApplication.shared.open(url). A relative href resolves
 * to capacitor://localhost/guide.html — a scheme iOS cannot open — so the tap
 * is silently dropped. An absolute https URL opens in Safari instead. On the
 * web the relative path is kept (same origin, no hop).
 */
export const publicUrl = (path: string): string =>
  isNative() ? PUBLIC_ORIGIN + (path.startsWith('/') ? path : '/' + path) : path;

// ---------------------------------------------------------------- Haptics

/** Soft tick — step toggles, small confirmations. */
export async function hapticTap(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {
    /* haptics unavailable — fine */
  }
}

/** Warm success buzz — completing a task, finishing a sort. */
export async function hapticSuccess(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch {
    /* haptics unavailable — fine */
  }
}

// ------------------------------------------------------------------ Share

/**
 * Share text via the native sheet when available; falls back to the Web
 * Share API, then to clipboard. Returns how it was delivered.
 */
export async function shareText(
  title: string,
  text: string,
): Promise<'native' | 'web' | 'clipboard' | 'failed'> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text });
      return 'native';
    } catch {
      /* user cancelled or plugin missing — try web paths */
    }
  }
  try {
    if (navigator.share) {
      await navigator.share({ title, text });
      return 'web';
    }
  } catch {
    /* cancelled — fall through to clipboard */
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'clipboard';
  } catch {
    return 'failed';
  }
}

// ---------------------------------------------------------- Notifications

const WEEKLY_ID = 1001;
const DAILY_ID = 1002;

/** Ask for notification permission. True when granted. */
export async function ensureNotificationPermission(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const status = await LocalNotifications.checkPermissions();
    if (status.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Gentle Sunday-evening nudge to run the weekly wrap-up. Tone matters:
 * an invitation, never an alarm or a guilt trip.
 */
export async function setWeeklyReminder(enabled: boolean): Promise<void> {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: [{ id: WEEKLY_ID }] });
    if (!enabled) return;
    await LocalNotifications.schedule({
      notifications: [
        {
          id: WEEKLY_ID,
          title: 'New week, fresh matrix',
          body: 'Whenever you have five minutes, wrap up last week and roll into the new one. No rush.',
          schedule: { on: { weekday: 1, hour: 18, minute: 0 }, allowWhileIdle: true },
        },
      ],
    });
  } catch {
    /* notifications unavailable — setting simply has no effect */
  }
}

/** Optional once-a-day "one thing" nudge (10:00). Off by default. */
export async function setDailyNudge(enabled: boolean): Promise<void> {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_ID }] });
    if (!enabled) return;
    await LocalNotifications.schedule({
      notifications: [
        {
          id: DAILY_ID,
          title: 'One thing today',
          body: 'Pick a single Zone 1 or Zone 2 task. Just one is a win.',
          schedule: { on: { hour: 10, minute: 0 }, allowWhileIdle: true },
        },
      ],
    });
  } catch {
    /* notifications unavailable */
  }
}

// ------------------------------------------------------------- App shell

/**
 * One-time native boot: hide the splash once React is painted and match
 * the status bar to the active theme. Call again on theme changes.
 */
export async function syncNativeChrome(dark: boolean): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: dark ? '#020617' : '#f1f5f9' });
    }
  } catch {
    /* status bar plugin missing */
  }
}

export async function hideSplash(): Promise<void> {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch {
    /* splash plugin missing */
  }
}

// ------------------------------------------------------------ App review

/**
 * Native in-app review dialog (Google Play In-App Review / iOS StoreKit).
 * The OS decides whether to actually show it and enforces its own quotas;
 * our policy layer (engine/review.ts) decides whether to even ask.
 * Silent no-op on web.
 */
export async function requestStoreReview(): Promise<void> {
  if (!isNative()) return;
  try {
    const { InAppReview } = await import('@capacitor-community/in-app-review');
    await InAppReview.requestReview();
  } catch {
    /* review plugin missing or OS declined — never surface this */
  }
}
